import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useSaveIntegration } from '@/api/integrations/use-integrations';
import type { IntegrationType, TenantIntegration } from '@/api/integrations/integrationService';
import type { CatalogProvider } from './catalog';
import {
    buildFieldsSchema,
    defaultFormValues,
    parseServerErrors,
    toConfiguration,
    type FormValues,
} from './integrationHelpers';

interface UseProviderFormOptions {
    type: IntegrationType;
    provider: CatalogProvider;
    /** The saved row of this provider, when there is one. */
    row?: TenantIntegration;
}

export interface ProviderFormState {
    form: UseFormReturn<FormValues>;
    isSaving: boolean;
    /** A 422 / network message that doesn't belong to a single field. */
    generalError?: string;
    /** Validates, saves and (when `enabled` is given) reconciles the on/off state. */
    save: (enabled?: boolean) => Promise<TenantIntegration | undefined>;
}

/**
 * react-hook-form + zod for one provider of one channel. The schema comes from the
 * catalog fields; backend 422 `errors` land on the matching field.
 */
export function useProviderForm({
    type,
    provider,
    row,
}: UseProviderFormOptions): ProviderFormState {
    const { t } = useTranslation('crm');
    const { enqueueSnackbar } = useSnackbar();
    const saveMutation = useSaveIntegration();
    const [generalError, setGeneralError] = useState<string | undefined>();

    const schema = useMemo(
        () =>
            buildFieldsSchema(provider.fields, {
                required: t('integrations.validation.required'),
                email: t('integrations.validation.email'),
                url: t('integrations.validation.url'),
                number: t('integrations.validation.number'),
                option: t('integrations.validation.option'),
            }),
        [provider.fields, t]
    );

    const defaults = useMemo(
        () => defaultFormValues(provider.fields, row?.configuration),
        [provider.fields, row?.configuration]
    );

    const form = useForm<FormValues>({
        resolver: zodResolver(schema),
        defaultValues: defaults,
    });

    // A save (or another admin) changed the stored values: show them, masked.
    useEffect(() => {
        form.reset(defaults);
    }, [defaults, form]);

    const save = useCallback(
        (enabled?: boolean) =>
            new Promise<TenantIntegration | undefined>(resolve => {
                setGeneralError(undefined);
                void form.handleSubmit(
                    async values => {
                        try {
                            const saved = await saveMutation.mutateAsync({
                                existing: row,
                                type,
                                provider: provider.id,
                                configuration: toConfiguration(
                                    provider.fields,
                                    values,
                                    row?.configuration
                                ),
                                enabled,
                            });
                            enqueueSnackbar(
                                t('integrations.toasts.saved', { name: provider.name }),
                                {
                                    variant: 'success',
                                }
                            );
                            resolve(saved);
                        } catch (error) {
                            const { fields, general } = parseServerErrors(
                                error,
                                provider.fields.map(field => field.key)
                            );
                            Object.entries(fields).forEach(([key, message]) =>
                                form.setError(key, { type: 'server', message })
                            );
                            setGeneralError(general ?? t('integrations.toasts.saveFailed'));
                            resolve(undefined);
                        }
                    },
                    () => resolve(undefined)
                )();
            }),
        [enqueueSnackbar, form, provider, row, saveMutation, t, type]
    );

    return { form, isSaving: saveMutation.isPending, generalError, save };
}
